import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import Table from "$components/table";
import { initiativesApi } from "../api";
import { initiateModal } from "$stores/modal";
import { InitiativeDetailsStore, setActionItemsCount } from "$stores/initiative-details";
import AddActionItemModal from "./AddActionItemModal";
import EditActionItemModal from "./EditActionItemModal";
import { buildActionItemColumns } from "./libs/action-item-columns";

const STATUS_ORDER = { open: 0, complete: 1, cancelled: 2 };

const defaultSort = (items) =>
    [...items].sort((a, b) => {
        const aStatus = STATUS_ORDER[a.status] ?? 3;
        const bStatus = STATUS_ORDER[b.status] ?? 3;
        if (aStatus !== bStatus) return aStatus - bStatus;
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate) - new Date(b.dueDate);
    });

const applySort = (items, sort) => {
    const [col] = Object.keys(sort);
    if (!col) return items;
    const dir = sort[col] === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
        let aVal = a[col] ?? "";
        let bVal = b[col] ?? "";
        if (col === "dueDate") {
            aVal = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
            bVal = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        } else if (typeof aVal === "string") {
            aVal = aVal.toLowerCase();
            bVal = bVal.toLowerCase();
        }
        if (aVal < bVal) return -dir;
        if (aVal > bVal) return dir;
        return 0;
    });
};

const ActionItemsTab = () => {
    const { id: initiativeId } = useParams();
    const collaborators = InitiativeDetailsStore.useState((s) => s.collaborators);
    const [followUps, setFollowUps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sort, setSort] = useState({});
    const [statusFilter, setStatusFilter] = useState("");
    const [assigneeFilter, setAssigneeFilter] = useState("");

    const load = useCallback(() => {
        const controller = new AbortController();
        initiativesApi
            .getFollowUps(initiativeId, controller.signal)
            .then((res) => {
                if (controller.signal.aborted) return;
                const data = res || [];
                setFollowUps(defaultSort(data));
                setActionItemsCount(data.filter((f) => f.status === "open").length);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return controller;
    }, [initiativeId]);

    useEffect(() => {
        const controller = load();
        return () => controller.abort();
    }, [load]);

    const handleStatus = async (followUp, newStatus) => {
        const prev = followUp.status;
        setFollowUps((fs) =>
            defaultSort(fs.map((f) => (f.id === followUp.id ? { ...f, status: newStatus } : f)))
        );
        try {
            await initiativesApi.updateFollowUp(initiativeId, followUp.id, {
                status: newStatus,
                commitmentText: followUp.commitmentText,
                dueDate: followUp.dueDate || null,
                clearDueDate: false,
                assignedToUserId: followUp.assignedToUserId || null,
                clearAssignedUser: false,
            });
            setFollowUps((fs) => {
                setActionItemsCount(fs.filter((f) => f.status === "open").length);
                return fs;
            });
        } catch {
            setFollowUps((fs) =>
                defaultSort(fs.map((f) => (f.id === followUp.id ? { ...f, status: prev } : f)))
            );
        }
    };

    const handleEdit = async (followUp, updates) => {
        await initiativesApi.updateFollowUp(initiativeId, followUp.id, {
            status: followUp.status,
            ...updates,
        });
        const assigneeName = updates.assignedToUserId
            ? collaborators.find((c) => c.userId === updates.assignedToUserId)?.userName ?? null
            : null;
        setFollowUps((fs) =>
            defaultSort(
                fs.map((f) =>
                    f.id === followUp.id
                        ? {
                            ...f,
                            commitmentText: updates.commitmentText,
                            dueDate: updates.clearDueDate ? null : updates.dueDate ?? f.dueDate,
                            assignedToUserId: updates.assignedToUserId || null,
                            assignedToUserName: updates.assignedToUserId ? assigneeName : null,
                          }
                        : f
                )
            )
        );
    };

    const openAddModal = () => {
        initiateModal(AddActionItemModal, { initiativeId, onSave: () => load() });
    };

    const openEditModal = (followUp) => {
        initiateModal(
            EditActionItemModal,
            {
                item: followUp,
                collaborators,
                onSave: (updates) => handleEdit(followUp, updates),
                onCancelItem: () => handleStatus(followUp, "cancelled"),
            },
            { analyticsLabel: "Edit Action Item" }
        );
    };

    const columns = useMemo(
        () => buildActionItemColumns({
            onMarkComplete: (row) => handleStatus(row, "complete"),
            onEdit: openEditModal,
        }),
        [collaborators] // eslint-disable-line
    );

    const assigneeOptions = useMemo(
        () => [...new Set(followUps.map((f) => f.assignedToUserName).filter(Boolean))].sort(),
        [followUps]
    );

    const tableData = useMemo(() => {
        let items = Object.keys(sort).length ? applySort(followUps, sort) : followUps;
        if (statusFilter) items = items.filter((f) => f.status === statusFilter);
        if (assigneeFilter) items = items.filter((f) => f.assignedToUserName === assigneeFilter);
        return items.map((f) => ({ key: f.id, ...f }));
    }, [followUps, sort, statusFilter, assigneeFilter]);

    return (
        <>
            <div className="action-items-tab__header">
                <div className="action-items-tab__filters">
                    <select
                        className="action-items-tab__filter-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">All statuses</option>
                        <option value="open">Open</option>
                        <option value="complete">Complete</option>
                        <option value="cancelled">Cancelled</option>
                    </select>
                    {assigneeOptions.length > 0 && (
                        <select
                            className="action-items-tab__filter-select"
                            value={assigneeFilter}
                            onChange={(e) => setAssigneeFilter(e.target.value)}
                        >
                            <option value="">All assignees</option>
                            {assigneeOptions.map((name) => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </select>
                    )}
                </div>
                <button className="button -primary -small" onClick={openAddModal}>
                    + Add
                </button>
            </div>

            <div className="action-items-tab">
                <Table
                    allowFilter={false}
                    columns={columns}
                    data={tableData}
                    hiddenColumns={[]}
                    isLoading={loading}
                    showPagination={false}
                    sort={sort}
                    setSort={setSort}
                />
            </div>
        </>
    );
};

export default ActionItemsTab;
